import { useState, useEffect, useRef, useCallback } from "react";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
const getCurrentWeekDates = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - dayOfWeek);
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  return {
    start: sunday.toISOString().split("T")[0],
    end: saturday.toISOString().split("T")[0],
  };
};

export const useHoursReport = () => {
  const { getAuthHeaders, user } = useAuth();
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentWeek = getCurrentWeekDates();
  const [startDate, setStartDate] = useState(currentWeek.start);
  const [endDate, setEndDate] = useState(currentWeek.end);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [payrollExportDialogOpen, setPayrollExportDialogOpen] = useState(false);
  const printRef = useRef(null);
  const [reportTypeDialogOpen, setReportTypeDialogOpen] = useState(false);

  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [editForm, setEditForm] = useState({
    hours: "",
    date: "",
    materials: [{ material_id: "", quantity: "" }],
  });
  const [submitting, setSubmitting] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);

  const canEdit = user?.role === "admin" || user?.role === "project_manager" || user?.role === "office_manager";

  const sortedMaterials = [...materials].sort((a, b) => {
    const catCompare = (a.category || "").localeCompare(b.category || "");
    if (catCompare !== 0) return catCompare;
    return (a.name || "").localeCompare(b.name || "");
  });

  const materialsByCategory = sortedMaterials.reduce((acc, mat) => {
    const category = mat.category || "Other";
    if (!acc[category]) acc[category] = [];
    acc[category].push(mat);
    return acc;
  }, {});

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/reports/hours`;
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await apiClient.get(url);
      setReport(response.data);
    } catch {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, getAuthHeaders]);

  const fetchEmployeesAndMaterials = useCallback(async () => {
    try {
      const [empRes, matRes] = await Promise.all([
        apiClient.get(`/employees`),
        apiClient.get(`/materials`),
      ]);
      setEmployees(empRes.data);
      setMaterials(matRes.data);
    } catch {
      console.error("Failed to load employees/materials");
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchReport();
    fetchEmployeesAndMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditClick = (jobId, jobName, employeeId, employeeName, hoursByDay, week) => {
    const datesWithHours = week.filter((d) => hoursByDay[d] > 0).map((d) => ({
      date: d,
      hours: hoursByDay[d],
    }));
    setAvailableDates(datesWithHours);
    setEditingEntry({ jobId, jobName, employeeId, employeeName, hoursByDay });
    setEditForm({ hours: "", date: "", materials: [{ material_id: "", quantity: "" }] });
    setEditDialogOpen(true);
  };

  const handleDateSelect = (selectedDate) => {
    if (editingEntry?.hoursByDay) {
      const hours = editingEntry.hoursByDay[selectedDate] || 0;
      setEditForm({ ...editForm, date: selectedDate, hours: hours.toString() });
    }
  };

  const handleDeleteEntry = async (jobId, employeeId, date) => {
    try {
      const entriesRes = await apiClient.get(`/daily-entries?job_id=${jobId}`);
      const entry = entriesRes.data.find((e) =>
        e.date === date && e.employees.some((emp) => emp.employee_id === employeeId)
      );
      if (entry) {
        await apiClient.delete(`/daily-entries/${entry.id}`);
        toast.success("Entry deleted");
        fetchReport();
      } else {
        // No daily entry found — try removing orphaned labor cost directly from the job
        try {
          await apiClient.delete(`/daily-entries/job/${jobId}/labor-cost?employee_id=${employeeId}&date=${date}`);
          toast.success("Orphaned entry removed");
          fetchReport();
        } catch {
          toast.error("Entry not found");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete entry");
    }
  };

  const handleSaveEdit = async () => {
    if (!editingEntry || !editForm.date) {
      toast.error("Please select a date to edit");
      return;
    }
    setSubmitting(true);
    try {
      const entriesRes = await apiClient.get(`/daily-entries?job_id=${editingEntry.jobId}`);
      const entry = entriesRes.data.find((e) =>
        e.date === editForm.date && e.employees.some((emp) => emp.employee_id === editingEntry.employeeId)
      );
      if (entry) {
        const updatedEmployees = entry.employees.map((emp) => {
          if (emp.employee_id === editingEntry.employeeId) {
            return {
              ...emp,
              employee_id: emp.employee_id,
              hours: parseFloat(editForm.hours),
              materials: editForm.materials
                .filter((m) => m.material_id && parseFloat(m.quantity) > 0)
                .map((m) => ({ material_id: m.material_id, quantity: parseFloat(m.quantity) })),
            };
          }
          return { employee_id: emp.employee_id, hours: emp.hours, materials: emp.materials || [] };
        });
        await apiClient.put(`/daily-entries/${entry.id}`, {
          job_id: entry.job_id, date: entry.date, notes: entry.notes || "", employees: updatedEmployees,
        });
        toast.success("Entry updated successfully");
        setEditDialogOpen(false);
        setEditingEntry(null);
        setAvailableDates([]);
        fetchReport();
      } else {
        toast.error("Entry not found");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update entry");
    } finally {
      setSubmitting(false);
    }
  };

  const getWeeksData = useCallback(() => {
    const allDates = new Set();
    report.forEach((job) => {
      job.employees.forEach((emp) => {
        Object.keys(emp.hours_by_day).forEach((d) => allDates.add(d));
      });
    });
    const sortedDates = Array.from(allDates).sort();
    if (sortedDates.length === 0) return [];

    const firstDate = new Date(sortedDates[0] + "T00:00:00");
    const lastDate = new Date(sortedDates[sortedDates.length - 1] + "T00:00:00");
    const startSunday = new Date(firstDate);
    startSunday.setDate(firstDate.getDate() - firstDate.getDay());
    const endSaturday = new Date(lastDate);
    endSaturday.setDate(lastDate.getDate() + (6 - lastDate.getDay()));

    const weeks = [];
    let currentDate = new Date(startSunday);
    while (currentDate <= endSaturday) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(currentDate.toISOString().split("T")[0]);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  }, [report]);

  const formatDate = useCallback((dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${m}/${d}`;
  }, []);

  const getWeekRange = useCallback((week) => `${formatDate(week[0])} - ${formatDate(week[6])}`, [formatDate]);

  const resetToThisWeek = () => {
    const week = getCurrentWeekDates();
    setStartDate(week.start);
    setEndDate(week.end);
    setTimeout(fetchReport, 0);
  };

  const getExcelData = useCallback(() => {
    const weeks = getWeeksData();
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    if (report.length === 0 || weeks.length === 0) {
      toast.error("No data to export. Please ensure there are hours recorded.");
      return null;
    }
    let csvRows = [];
    report.forEach((job) => {
      csvRows.push([`Job: ${job.job_name}`]);
      weeks.forEach((week) => {
        csvRows.push([`Week: ${getWeekRange(week)}`]);
        csvRows.push(["Employee", ...dayNames, "Weekly Total"]);
        job.employees.forEach((emp) => {
          const weekHours = week.map((d) => emp.hours_by_day[d] || 0);
          const weekTotal = weekHours.reduce((a, b) => a + b, 0);
          if (weekTotal > 0) csvRows.push([emp.employee_name, ...weekHours, weekTotal]);
        });
        csvRows.push([]);
      });
      csvRows.push([]);
    });
    return { filename: "hours-report", csvContent: csvRows.map((row) => row.join(",")).join("\n") };
  }, [report, getWeeksData, getWeekRange]);

  const getPdfContent = useCallback(() => {
    const weeks = getWeeksData();
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    if (report.length === 0 || weeks.length === 0) {
      toast.error("No data to export. Please ensure there are hours recorded.");
      return null;
    }
    const customStyles = `
      .job-card { border: 1px solid #ddd; border-radius: 8px; margin-bottom: 20px; overflow: hidden; page-break-inside: avoid; break-inside: avoid; }
      .job-header { background: #f5f5f5; padding: 12px 16px; font-size: 18px; font-weight: bold; border-bottom: 1px solid #ddd; }
      .week-header { background: #e5e7eb; padding: 8px 16px; font-size: 12px; color: #666; font-weight: 500; }
      .week-section { page-break-inside: avoid; break-inside: avoid; }
      .hours-cell { background: #eff6ff; color: #2563eb; font-weight: 500; padding: 4px 8px; border-radius: 4px; display: inline-block; min-width: 30px; text-align: center; }
      .total-row { background: #f0fdf4; font-weight: bold; }
      .job-total { background: #eff6ff; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; }
      .job-total-label { font-weight: 600; }
      .job-total-value { font-size: 20px; font-weight: bold; color: #2563eb; }
    `;
    const htmlContent = `
      <h1>Hours Report</h1>
      <p class="subtitle">Employee hours by job (Sunday - Saturday) | Generated ${new Date().toLocaleDateString()}</p>
      ${report.map(job => {
        const jobWeeks = weeks.filter(week => job.employees.some(emp => week.some(d => emp.hours_by_day[d] > 0)));
        return `
          <div class="job-card">
            <div class="job-header">${job.job_name}</div>
            ${jobWeeks.map(week => `
              <div class="week-section">
                <div class="week-header">Week of ${getWeekRange(week)}</div>
                <table>
                  <thead><tr><th>Employee</th>${dayNames.map((day, i) => `<th class="text-center">${day}<br/><small>${formatDate(week[i])}</small></th>`).join('')}<th class="text-right">Total</th></tr></thead>
                  <tbody>
                    ${job.employees.map(emp => {
                      const weekHours = week.map(d => emp.hours_by_day[d] || 0);
                      const weekTotal = weekHours.reduce((a, b) => a + b, 0);
                      if (weekTotal === 0) return '';
                      return `<tr><td>${emp.employee_name}</td>${weekHours.map(h => `<td class="text-center">${h > 0 ? `<span class="hours-cell">${h}</span>` : '-'}</td>`).join('')}<td class="text-right" style="font-weight: bold; color: #2563eb;">${weekTotal}</td></tr>`;
                    }).join('')}
                    <tr class="total-row"><td>Daily Totals</td>${week.map(date => {
                      const dayTotal = job.employees.reduce((sum, emp) => sum + (emp.hours_by_day[date] || 0), 0);
                      return `<td class="text-center">${dayTotal > 0 ? dayTotal : '-'}</td>`;
                    }).join('')}<td class="text-right">${job.employees.reduce((sum, emp) => sum + week.reduce((s, d) => s + (emp.hours_by_day[d] || 0), 0), 0)}</td></tr>
                  </tbody>
                </table>
              </div>
            `).join('')}
            <div class="job-total"><span class="job-total-label">Job Total Hours</span><span class="job-total-value">${job.employees.reduce((sum, emp) => sum + emp.total_hours, 0)}</span></div>
          </div>
        `;
      }).join('')}
      <p class="footer">V2R Masonry - Hours Report</p>
    `;
    return { title: "Hours Report", htmlContent, customStyles };
  }, [report, getWeeksData, getWeekRange, formatDate]);

  const getPayrollPdfContent = useCallback(() => {
    if (report.length === 0) {
      toast.error("No data to export. Please ensure there are hours recorded.");
      return null;
    }
    const allDates = new Set();
    report.forEach((job) => {
      job.employees.forEach((emp) => {
        Object.keys(emp.hours_by_day).forEach((d) => allDates.add(d));
      });
    });
    const sortedDates = Array.from(allDates).sort();
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Build employee totals
    const empTotals = {};
    const dailyBreakdown = {};
    const jobTotals = {};
    report.forEach((job) => {
      let jobTotal = 0;
      job.employees.forEach((emp) => {
        empTotals[emp.employee_name] = (empTotals[emp.employee_name] || 0) + emp.total_hours;
        if (!dailyBreakdown[emp.employee_name]) dailyBreakdown[emp.employee_name] = {};
        sortedDates.forEach((d) => {
          const h = emp.hours_by_day[d] || 0;
          dailyBreakdown[emp.employee_name][d] = (dailyBreakdown[emp.employee_name][d] || 0) + h;
          jobTotal += h;
        });
      });
      jobTotals[job.job_name] = (jobTotals[job.job_name] || 0) + jobTotal;
    });

    const totalHours = Object.values(empTotals).reduce((a, b) => a + b, 0);
    const sortedEmps = Object.entries(empTotals).sort((a, b) => b[1] - a[1]);
    const sortedJobs = Object.entries(jobTotals).sort((a, b) => b[1] - a[1]).slice(0, 10);

    const fmtShortDate = (ds) => {
      const dt = new Date(ds + "T00:00:00");
      return `${dayNames[dt.getDay()]} ${String(dt.getMonth() + 1).padStart(2, "0")}/${String(dt.getDate()).padStart(2, "0")}`;
    };

    const customStyles = `
      .summary-table { margin-bottom: 20px; }
      .summary-table td, .summary-table th { padding: 6px 12px; }
      .section-title { font-size: 14px; font-weight: bold; margin: 16px 0 8px; }
      .total-badge { font-size: 16px; font-weight: bold; margin-bottom: 12px; }
      .breakdown-table td, .breakdown-table th { padding: 4px 8px; font-size: 11px; }
      .breakdown-table th { white-space: nowrap; }
    `;

    const htmlContent = `
      <h1>Payroll Report</h1>
      <p class="subtitle">${startDate} to ${endDate} | Generated ${new Date().toLocaleDateString()}</p>
      <p class="total-badge">Total Hours: ${totalHours.toFixed(1)}</p>
      <div class="section-title">Employee Hours</div>
      <table class="summary-table">
        <thead><tr><th>Employee</th><th class="text-right">Total Hours</th></tr></thead>
        <tbody>${sortedEmps.map(([name, hrs]) => `<tr><td>${name}</td><td class="text-right">${hrs.toFixed(1)}</td></tr>`).join("")}</tbody>
      </table>
      <div class="section-title">Top Jobs by Hours</div>
      <table class="summary-table">
        <thead><tr><th>Job</th><th class="text-right">Total Hours</th></tr></thead>
        <tbody>${sortedJobs.map(([name, hrs]) => `<tr><td>${name}</td><td class="text-right">${hrs.toFixed(1)}</td></tr>`).join("")}</tbody>
      </table>
      <div style="page-break-before: always;"></div>
      <h2>Daily Breakdown</h2>
      <table class="breakdown-table">
        <thead><tr><th>Employee</th>${sortedDates.map((d) => `<th class="text-center">${fmtShortDate(d)}</th>`).join("")}<th class="text-right">Total</th></tr></thead>
        <tbody>${Object.keys(dailyBreakdown).sort().map((emp) => {
          const empTotal = Object.values(dailyBreakdown[emp]).reduce((a, b) => a + b, 0);
          return `<tr><td>${emp}</td>${sortedDates.map((d) => {
            const h = dailyBreakdown[emp][d] || 0;
            return `<td class="text-center">${h > 0 ? h.toFixed(1) : ""}</td>`;
          }).join("")}<td class="text-right" style="font-weight:bold">${empTotal.toFixed(1)}</td></tr>`;
        }).join("")}</tbody>
      </table>
      <p class="footer">V2R Masonry - Payroll Report</p>
    `;

    return { title: "Payroll Report", htmlContent, customStyles };
  }, [report, startDate, endDate]);

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setAvailableDates([]);
    setEditingEntry(null);
  };

  const handlePayrollExport = async () => {
    try {
      const response = await apiClient.get(`/reports/payroll-export`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Payroll_Report_${startDate}_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Payroll report downloaded");
    } catch (error) {
      const detail = error.response?.status === 404 ? "No hours recorded for the selected period" : "Failed to generate payroll report";
      toast.error(detail);
    }
  };

  return {
    // Data
    report, loading, canEdit, printRef,
    startDate, endDate, editDialogOpen, editingEntry,
    editForm, submitting, availableDates, materialsByCategory,
    exportDialogOpen, reportTypeDialogOpen, payrollExportDialogOpen,
    // Setters
    setStartDate, setEndDate, setEditDialogOpen, setEditForm,
    setExportDialogOpen, setReportTypeDialogOpen, setPayrollExportDialogOpen,
    // Handlers
    fetchReport, handleEditClick, handleDateSelect, handleDeleteEntry,
    handleSaveEdit, handlePayrollExport, closeEditDialog, resetToThisWeek,
    // Export data generators
    getExcelData, getPdfContent, getPayrollPdfContent,
    // Computed
    getWeeksData, formatDate, getWeekRange,
  };
};
